import { useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { useStaffProfileDetail } from '@/features/members';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import styles from './StaffProfileDetailPage.module.css';

export function StaffProfileDetailPage() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const detail = useStaffProfileDetail(params.id);

  if (detail.isLoading) {
    return (
      <div className={styles.page} role="status">
        {t('Loading…')}
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page} role="alert">
        {t('This Staff Profile is not available.')}
      </div>
    );
  }

  const p = detail.data.item;
  const displayName = formatPersonName(p);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Avatar name={displayName} size="lg" kind="ai" />
        <div className={styles['header-text']}>
          <div className={styles['title-line']}>
            <h1 className={styles.title}>{displayName}</h1>
            <Chip variant="neutral" size="sm">
              {t('Linqage')}
            </Chip>
          </div>
          {p.description ? <p className={styles.subtitle}>{p.description}</p> : null}
        </div>
      </header>

      <section className={styles.defaults}>
        <div className={styles['defaults-row']}>
          <span className={styles.label}>
            {p.defaultPositions.length === 1 ? t('Default Position') : t('Default Positions')}
          </span>
          {p.defaultPositions.length === 0 ? (
            <span className={styles.empty}>—</span>
          ) : (
            <div className={styles['ref-list']}>
              {p.defaultPositions.map((pos) => (
                <button
                  key={pos.id}
                  type="button"
                  className={styles['ref-link']}
                  onClick={() => void navigate(`/library/positions/${encodeURIComponent(pos.id)}`)}
                >
                  {pos.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={styles['defaults-row']}>
          <span className={styles.label}>
            {p.defaultUnits.length === 1 ? t('Default Department') : t('Default Departments')}
          </span>
          {p.defaultUnits.length === 0 ? (
            <span className={styles.empty}>—</span>
          ) : (
            <div className={styles['ref-list']}>
              {p.defaultUnits.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className={styles['ref-link']}
                  onClick={() => void navigate(`/library/departments/${encodeURIComponent(u.id)}`)}
                >
                  {u.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {p.personality ? (
        <section className={styles.section}>
          <h3 className={styles.heading}>{t('How they work')}</h3>
          <p className={styles.prose}>{p.personality}</p>
        </section>
      ) : null}

      <section className={styles.section}>
        <h3 className={styles.heading}>{t('Detailed instructions')}</h3>
        <pre className={styles.instructions}>{p.systemPrompt}</pre>
      </section>

      <div className={styles.actions}>
        <Button variant="ghost" size="md" onClick={() => void navigate('/library/member-profiles')}>
          {t('Back')}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={() => void navigate('/team/add', { state: { preselectStaffProfileId: p.id } })}
        >
          {t('Add {name}', { name: p.first_name })}
        </Button>
      </div>
    </div>
  );
}
